import {
	Course,
	EventTemplateRule,
	Tag,
	Event,
	EventParticipation,
	GamificationContext,
	CourseExamParticipationReport,
	PublicCourseData,
	UserProgress,
	LearningPathData,
	TopicQuizConfig,
	GamificationEvent,
} from "@/models";
import axios from "axios";
import { convertEventTemplateRules } from "./utils";

export async function getCourses(): Promise<Course[]> {
	const response = await axios.get(`/courses/`);
	return response?.data ?? [];
}

export async function manageSelfCourseEnrollment(
	courseId: string,
	remove: boolean,
): Promise<void> {
	const method = remove ? "delete" : "put";
	const response = await axios[method](`/courses/${courseId}/my_enrollment/`);
	return response.data;
}

export async function setCourseBookmark(
	courseId: string,
	remove: boolean,
): Promise<Course> {
	const method = remove ? "delete" : "put";
	const response = await axios[method](`/courses/${courseId}/bookmark/`);
	return response.data;
}

export async function getCourseParticipationReport(
	courseId: string,
): Promise<CourseExamParticipationReport> {
	const ret = {};
	let page = 1;
	// eslint-disable-next-line no-constant-condition
	while (true) {
		const response = await axios.get(
			`/courses/${courseId}/participation_report/?page=${page}`,
		);

		if (Object.keys(response.data.results).length === 0) {
			return ret as CourseExamParticipationReport;
		}
		Object.assign(ret, response.data.results);

		page += 1;
	}
}

export async function getCourseGamificationContext(
	courseId: string,
): Promise<GamificationContext> {
	const response = await axios.get(`/courses/${courseId}/gamification_context/`);
	return response.data;
}

export async function getCourseUnstartedPracticeEvents(
	courseId: string,
): Promise<Event[]> {
	// TODO make action for this
	const response = await axios.get(`/courses/${courseId}/unstarted_practice_events/`);

	if (response.data.length > 0) {
		const event = response.data[0] as Event; // currently can only have one unstarted event
		const processedRules = convertEventTemplateRules(
			event.template?.rules as EventTemplateRule[],
		);

		const convertedEvent = {
			...event,
			template: {
				...event.template,
				rules: processedRules,
			},
		} as Event;

		return [convertedEvent];
	}

	return [];
}

export async function getCourse(courseId: string): Promise<Course> {
	const response = await axios.get(`/courses/${courseId}/`);

	// const { unstarted_practice_events, ...course } = response.data;
	// if (unstarted_practice_events && unstarted_practice_events.length > 0) {
	// 	const event = unstarted_practice_events[0] as Event; // currently can only have one unstarted event
	// 	const processedRules = convertEventTemplateRules(
	// 		event.template?.rules as EventTemplateRule[],
	// 	);

	// 	const convertedEvent = {
	// 		...event,
	// 		template: {
	// 			...event.template,
	// 			rules: processedRules,
	// 		},
	// 	} as Event;

	// 	return { ...course, unstarted_practice_events: [convertedEvent] };
	// }

	return response.data;
}

export async function createCourse(course: Course): Promise<Course> {
	const response = await axios.post(`/courses/`, course);
	return response.data;
}

export async function getUserCourseParticipations(
	courseId: string,
	userId: string,
): Promise<EventParticipation[]> {
	const response = await axios.get(
		`/courses/${courseId}/participations/?user_id=${userId}&include_event=true`,
	);
	return response.data;
}

export async function updateCourse(courseId: string, course: Course): Promise<Course> {
	const response = await axios.put(`/courses/${courseId}/`, course);
	return response.data;
}

export async function deleteCourse(courseId: string): Promise<void> {
	const response = await axios.delete(`/courses/${courseId}/`);
	return response.data;
}

export async function getTags(
	courseId: string,
	includeExerciseCount: boolean,
): Promise<Tag[]> {
	const response = await axios.get(
		`/courses/${courseId}/tags/${
			includeExerciseCount ? "?include_exercise_count=1" : ""
		}`,
	);
	return response.data;
}

// Public Course APIs

export async function getPublicCourseData(slug: string): Promise<PublicCourseData> {
	const response = await axios.get(`/public/courses/${slug}/`);

	// The backend should return a complete course structure
	// For now, adapt the response to match our expected format
	const courseData = response.data;

	return {
		course: courseData,
		instructor: {
			name: courseData.instructor?.name || "Course Instructor",
			bio: courseData.instructor?.bio,
			profile_image: courseData.instructor?.profile_image,
		},
		stats: {
			total_enrollments: courseData.stats?.total_enrollments || 0,
			average_completion_rate: courseData.stats?.average_completion_rate || 0,
			total_lessons: courseData.stats?.total_lessons || 0,
			total_duration_minutes: courseData.stats?.total_duration_minutes || 0,
		},
		preview_content: {
			sample_lessons: courseData.preview_content?.sample_lessons || [],
			syllabus_outline: courseData.preview_content?.syllabus_outline || [],
		},
	};
}

export async function enrollInPublicCourse(
	slug: string,
	redirectAfterEnrollment?: string,
): Promise<void> {
	const response = await axios.post(`/courses/enroll_by_slug/`, {
		slug,
		redirect_after_enrollment: redirectAfterEnrollment,
		enrollment_source: "public_website",
	});
	return response.data;
}

// Progress Tracking APIs

export async function getUserProgress(courseId: string): Promise<UserProgress> {
	const response = await axios.get(`/courses/${courseId}/public_progress/`);
	return response.data;
}

export async function updateUserProgress(
	courseId: string,
	nodeId: string,
	completed: boolean,
): Promise<{ gamification_events: GamificationEvent[] }> {
	if (!completed) {
		try {
			await axios.post(`/courses/${courseId}/nodes/${nodeId}/complete/`, { completed });
		} catch {
			// ignore errors on uncomplete
		}
		return { gamification_events: [] };
	}
	const response = await axios.post(`/courses/${courseId}/nodes/${nodeId}/complete/`, {
		completed,
	});
	return response.data || { gamification_events: [] };
}

export async function getLearningPathData(courseId: string): Promise<LearningPathData> {
	// This endpoint is not implemented yet, return basic structure
	throw new Error("Learning path endpoint not implemented");
}

// Quiz Generation APIs

export async function generateTopicQuiz(
	courseId: string,
	nodeId: string,
	config?: TopicQuizConfig,
): Promise<EventParticipation> {
	const response = await axios.post(
		`/courses/${courseId}/nodes/${nodeId}/start_quiz/`,
		config || {},
	);
	return response.data;
}

export interface QuizSlotSubmission {
	id: string;
	selected_choices?: string[];
	answer_text?: string;
}

export async function submitTopicQuiz(
	courseId: string,
	nodeId: string,
	slots: QuizSlotSubmission[],
): Promise<EventParticipation> {
	const response = await axios.post(`/courses/${courseId}/nodes/${nodeId}/submit_quiz/`, {
		slots,
	});
	return response.data;
}

export async function getTopicQuizConfig(
	courseId: string,
	topicId: string,
): Promise<TopicQuizConfig | null> {
	// This endpoint is not implemented yet, return null
	console.warn("Topic quiz config endpoint not implemented");
	return null;
}
